import { Component, OnInit, ViewChild, Inject, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FieldWrapper, FormlyField, FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { HttpClient } from '@angular/common/http';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, ValidationErrors } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MutaraFormControl } from '../dynamic';
import { DialogComponent } from '../dialog/dialog.component';
import { ContextService, LoginData } from '../../context.service';

@Component({
  selector: 'mutara-group-list',
  templateUrl: './group-list.component.html',
  styleUrls: ['./group-list.component.scss'],
  providers: [DatePipe],
  standalone: false
})
export class GroupListComponent extends FieldWrapper implements OnInit, AfterViewInit {
  // Local table event helpers
  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
  @ViewChild(MatSort) sort: MatSort | undefined;
  @ViewChild(MatTable) table: MatTable<any> | undefined;

  // Can set between vertical/horizontal via subscription
  isVertical: boolean = false;

  // The data is the form model
  tableData: MatTableDataSource<any> = new MatTableDataSource<any>([]); // Wrapper around the form model object (array + values) 
  // selection elements 
  _selectedRow: number = -1;
  lookupLabels: any = {};
  // Data from the template
  dataColumns: FormlyFieldConfig[] = [];
  displayColumns: FormlyFieldConfig[] = [];
  rawData: any[] = [];
  listFields: string[] = [];
  buttons: any = [];
  login: LoginData | undefined;
  titleDefn: any;
  isAdmin: boolean = false;
  subscriptions: Subscription[] = [];
  dateFields: FormlyFieldConfig[] = [
    {
      key: '_searchDate',
      fieldGroupClassName: 'display-flex',
      wrappers: ['group'],
      fieldGroup: [{
        key: '_startFilter',
        type: 'datepicker',
        className: 'flex-1',
        modelOptions: { updateOn: 'blur' },
        props: {
          label: 'Beginning',
          description: 'Date of initial data',

        }
      },
      {
        key: '_endFilter',
        type: 'datepicker',
        className: 'flex-1',
        modelOptions: { updateOn: 'blur' },
        props: {
          label: 'End',
          description: 'Date of end data',
        }
      }]
    }];
  _startDate: Date = new Date(0);
  _endDate: Date = new Date();
  dateModel: any = {};

  /**
   * Private injectors to help with formatting and interaction
   * @param http 
   * @param contextService 
   * @param datePipe 
   * @param dialog 
   */
  constructor(public http: HttpClient,
    public contextService: ContextService,
    private datePipe: DatePipe,
    private dialog: MatDialog, 
    private cdr: ChangeDetectorRef) {
    super();
  }

  ngAfterViewInit(): void {
    // Observable subject
    if (!this.props.dataSubject) {
      this.props.dataSubject = new BehaviorSubject([]);
    }


    // Setup the pagination and sorting
    this.tableData.sort = this.sort ?? null;
    // this.tableData.sortingDataAccessor = (item, property) => {
    //   switch (property) {
    //     case 'mav_class-time':
    //       return new Date(new Date('01-01-1900 ' + item['mav_class-time'].split(' ')[0].replace('AM', ' AM').replace('PM', ' PM')));
    //     case 'mav_class-formattedcost':
    //       return parseInt(item['mav_class-formattedcost'].replace('$', '').replace(',', ''));
    //     default:
    //       return item[property];
    //   }
    // };


    if (this.paginator) {
      this.tableData.paginator = this.paginator;
    }
    // Handles both a direct search and date range search

    this.tableData.filterPredicate = (data: any, filter: string) => {
      let found = false;
      if (filter == this.props.filterDate) {
        found = ((data[filter] ?? this._startDate) >= this._startDate && (data[filter] ?? this._endDate) <= this._endDate);
      } else {
        found = Object.entries(data).reduce((isFound, value) => isFound ||= (<string>value[1]).trim().toLowerCase().includes(filter), found);
      }
      return found;
    }
    this.props.dataSubject.subscribe((displayData: any[] ) => {
      this.tableData.data = displayData.filter(data => !data._hide);
      //this.cdr.markForCheck();
      //console.log('DataSubject, display data %O rows', this.tableData.data.length);
    });

    this.setData(this.model);
  }
  /**
   * Use take the info from the base class to create the table
   */
  ngOnInit() {
    // Add a validator function for requried and precision
    this.formControl.setValidators(this._validation);
    if (!!this.props.onSelect && typeof this.props.onSelect != 'function') {
      this.props.onSelect = new Function('formState', 'value', 'context', 'http', this.props.onSelect);
    }
    if (this.field.fieldGroup) {
      // This table helps insert label values on remotely looked up values (service based lookups, cascading, etc.)
      this.field.fieldGroup.forEach((field: FormlyFieldConfig) => {
        field.props?.rdv?.displayValue.subscribe((label: string) => {
          if (!!field.props?.rdv.fieldId) {
            this.lookupLabels[field.props.rdv?.fieldId] = label ?? '';
          }
        });
      });
    }
    this.subscriptions.push(this.contextService.subscribeTo("selectedData", {
      next: (data: any) => {
        if (!data) {
          this._unselectValues();
          this.formState.groups[<string>this.field.key].selected = null;
        }
      }
    }));
    this.subscriptions.push(this.contextService.subscribeTo("loginData", { next: (login: LoginData) => this.login = login }));
    // Filter the configs props into data values & buttons
    this.dataColumns = this.field?.fieldGroup?.filter(fc => !fc.props?.buttons) ?? [];
    this.displayColumns = this.dataColumns.filter(dc => !dc.props?.hideSelf(this.formState, dc) && !dc.props?.hidden && !dc.props?.exportOnly);
    if ((this.props?.maxLength ?? -1) > 0) {
      this.displayColumns = this.displayColumns.slice(0, this.props.maxLength);
    }
    this.buttons = this.field?.fieldGroup?.find((fc: FormlyFieldConfig) => !!fc.props?.buttons)?.props?.buttons ?? [];
    // Subscribe to the orientation
    this.subscriptions.push(this.contextService.subscribeTo("orientation", {
      next: (isVertical: boolean) => {
        this.isVertical = isVertical || this.contextService.isMobile();
        this.listFields = <string[]>this.displayColumns.map((fc) => { return fc.key; });
        this.cdr.markForCheck();
      }
    }));
    this.titleDefn = this.props._title;
  }

  ngOnDestroy() {
    this.subscriptions.forEach( sub => sub.unsubscribe );
    this.subscriptions = [];
  }
  get visibleFields() {
    let max = this.isVertical ? 2 : ((this.props?.maxLength ?? -1) > 0 ? this.props.maxLength : this.displayColumns.length);
    return this.displayColumns.slice(0, max);
  }
  get isDelegated() {
    return !!this.contextService?.delegatedLogin;
  }
  /**
   * Make sure the returned data is mapped into the local model
   * @param rawData 
   */
  setData(rawData: any[], save: boolean = true): void {
    Object.assign((<MutaraFormControl>this.formControl), { precision: this.field?.props?.precision ?? 0, size: rawData?.length ?? 0 });
    let displayData: any[] = [];
    if (!!rawData && rawData?.length > 0) {
      try {
        // Add empty entries for the More... button
        let columnList = this.dataColumns.filter(dc => !dc.props?.exportOnly);
        console.log('Raw Data Length %O', rawData.length );      
        rawData.forEach((row, index) => {
          // Look for the keys of this row
          row._index = index;
          this.field?.fieldGroup?.filter(fc => !!fc.props?.rdv && fc.props?.saveField || fc.type == 'datepicker').forEach(fc => {
            switch (fc.type) {
              case 'datepicker':
                let old = row[<string>fc.key];
                if (!!old && typeof old === 'string') {
                  row[<string>fc.key] = new Date(old);
                }
                break;
              default:
                // In the case of saveField, replace the value with the saved one
                row[fc.props?.rdv.fieldId] = row[fc.props?.saveField.split('.', 2)[1]];
            }
          });
          let newRow: any = { _index: row._index, _hide: row._hide };
          for (const [key, value] of Object.entries(row)) {
            if (columnList.find(dc => dc.key == key)) {
              newRow[key] = value;
            }
          }
          displayData.push(newRow);
        });
        // If we are manipulating data for display, do not save this row set
        if (save) {
          this.rawData = [...rawData];
          this.props.rawData = this.rawData;
        }

      } catch (e) {
        console.error('Problem with setData %O %O', rawData, e);
      }
    }
    this.props.dataSubject.next(displayData);
  }
  /** 
   * Validate the row count (called by form)
   */
  _validation(control: any): ValidationErrors {
    let error: any;
    if (control?.precision ?? 0 > this.rawData?.length) {
      error = {};
      error.rowcount = `${control?.precision - (this.rawData?.length ?? 0)} too few entries for ${this.props?.label}`;
    }
    return error;
  }

  isValid(field: FormlyFieldConfig): boolean {
    if (field.key) {
      return !!field.formControl?.valid;
    }
    return field.fieldGroup ? field.fieldGroup.every((f) => this.isValid(f)) : true;
  }

  get isHidden(): boolean {
    return !!this.field.hide;
  }
  /**
   *  Reset the selection
   */
  _unselectValues(): void {
    // clear previous selection
    if (this._selectedRow != -1 && !!this.rawData[this._selectedRow]) {
      //this.tableData.data[this._selectedRow]._selected = false;
    }
    this._selectedRow = -1;

    this.formState.groups[<string>this.field.key].selected = null;
  }

  /**
   * remember which row one we are modifying (called by template)
   * @param row 
   */
  _selectRow(index: number, suppress: boolean): void {
    let row = this.tableData?.data[index];
    this.formControl.reset(); // Clear the form fields

    if (this.formState.groups[<string>this.field.key].props?.detailsOnRowClick && !suppress) {
      this.showAdditionalData(row, 'detail');
    }

    let prevSelectedIndex = this._selectedRow;
    this._unselectValues();
    if (index == prevSelectedIndex) {
      //row._selected = false;
      row = { ...row };
      Object.keys(row).forEach(key => row[key] = null);
    } else {
      // setup memory of current row in table
      this._selectedRow = index;
      this.formState.groups[<string>this.field.key].selected = this.tableData?.data[index];
      //row._selected = true;
    }
    // reset the options lists
    this.field.fieldGroup?.forEach((field: FormlyFieldConfig) => {
      // Reset the data based on the result row set of values
      if (field.props?.rdv) { field.props?.rdv?.setDisplayFromSet(row); }
    });

    // Patch the data row into the form
    this.formControl.patchValue(row); // Copy the values to the form fields
    if (!!this.props.onSelect) {
      try {
        this.props.onSelect(this.formState, this._selectedRow != -1, this.contextService, this.http);
      } catch (err) {
        console.error(err);
      }
    }
    // WIP for handling embedded user data
    if (!!this.titleDefn) {
      let data = { label: this.rawData[index][this.titleDefn.label], id: this.rawData[index][this.titleDefn.id] };
      this.contextService.selectedRow(data);
    }
  }

  /**
   * Helper to clear out the form data and render the table again
   */
  _resetForm(): void {
    if (!!this.paginator) {
      this.paginator.firstPage();
      this.table?.renderRows();
    }

    this._unselectValues();
    this.field.fieldGroup?.forEach((field: FormlyFieldConfig) => {
      if (field.props?.rdv) {
        field.props.rdv.setDisplay('');
      }
    });
    //this.formControl.updateValueAndValidity();
    this.formControl.reset(); // Clear the form fields
    this.cdr.markForCheck();
  }

  /**
  * Allow us to set the value associated with the buttons
  * @param value 
  */
  _setButtonValue(value: string): void {
    let newValues = this.formControl.value;
    newValues[this.field.key + '_add'] = value;
    //this.formControl.setValue(newValues);
  }

  /**
   * Update the data of the row for the mapping table (which has the sequence) to match the table data in the UI
   * @param parameters 
   */
  _addSequenceData(dataToSequence: any): void {
    let seqTable: FormlyFieldConfig | undefined = this.field?.fieldGroup?.find((fc: any) => {
      let fd: FormlyFieldConfig | undefined = fc;
      return (<string>fd?.key)?.startsWith('map') ?? false; //?.split('-');
    });
    if (!!seqTable) {
      dataToSequence.data.values[(<string>seqTable.key)?.split('-')[0] + '-sequence'] = (this._selectedRow == -1 ? this.tableData?.data.length ?? 0 : this._selectedRow);
    }
  }

  _dataOnly(rowData: any): any {
    let newData: any = {};
    for (const [key, value] of Object.entries(rowData)) {
      if (!key.startsWith('key.')) {
        newData[key] = value;
      }
    }
    return newData;
  }
  /**
   * Convert the current row to an appropriate parameter object for the server
   * @param typeName action 
   */
  _serverParameters(typeName: string, incomingModel: any = this.formControl.value) {
    this._setButtonValue(typeName);
    let parameters: any = {
      formName: this.props.formName,
      data: {
        group: this.key,
        type: typeName,
        keys: {},
      }
    };

    // Values are add in the CU cases
    if ('delete' != typeName) {
      let isUpdate = 'update' == typeName;

      parameters.data.values = this._selectedRow == -1 ? incomingModel : this._dataOnly(this.rawData[this._selectedRow]);

      this.field.fieldGroup?.forEach(fc => {
        if (fc.type == 'checkbox') {
          parameters.data.values[<string>fc.key] = !!parameters.data.values[<string>fc.key];
        }
        if (!!fc.props?.saveField) {
          let undecorated = fc.props.saveField.split('.', 2)[1];
          parameters.data.values[undecorated] = fc.props.rdv?.currentValue;
          if (isUpdate && !!fc.props.rdv) {
            delete parameters.data.values[<string>fc.props.rdv.fieldId];
          }
        }
      });
    }
    // using the fall-through to set the keys
    switch (typeName) {
      case 'update':
      case 'insert':
        this._addSequenceData(parameters);
      case 'delete':
        // if this is an insert and we need a map sequence, create it here. 
        if ('insert' != typeName) {
          // Here we add the keys from the existing model rows (not included in form)
          let rowData = this._selectedRow != -1 ? this.rawData[this._selectedRow] : null;
          for (const [keyName, value] of Object.entries(rowData)) {
            if (keyName.startsWith('key.')) {
              parameters.data.keys[keyName.slice(4)] = value;
            };
          }
        }
        break;
    }
    return parameters;
  }

  get isHideSearch(): boolean {
    return (this.props?.hideSearch ?? true);
  }

  /** 
   * Handle new and updated values 
   */
  newValue(action = '$service/formdata', data = this.formControl.value): void {

    // Only work with valid form data
    if (this.isVertical) {
      this.showAdditionalData(data, 'more');
      return;
    }
    if (this.formControl.invalid) {
      let invalidControls: string[] = [];
      this.field.fieldGroup?.forEach((field: FormlyFieldConfig) => {
        if (!!field.formControl?.invalid && field.props?.label) {
          invalidControls.push(field.props.label);
        }
      });
      this.contextService.changeMessage(`Invalid value${invalidControls.length > 1 ? 's' : ''} for ${invalidControls.join(', ')}`);
      return;
    }
    // This sets up the request parameters from the form data (only stuff that can change)
    let row = this._selectedRow;
    // Get any values that need to be updated
    if (row > -1) {
      if (this.rawData.length < row + 1) {
        console.log('Expand model for row %O', row);
        this.rawData.length = row + 1;
      }
      // Set the updated data into the model (this catches the labels)
      Object.assign(this.rawData[row], data ?? {}, this.lookupLabels);
    } else { // Add a new row 
      // need to get any values in the typeaheads... 
      if (this.rawData.length) {
        this._selectedRow = row = this.rawData.push(Object.assign(this.field.form?.value[<string>this.field.key] ?? {}, this.lookupLabels)) - 1;
      } else {
        let localArray = [];
        localArray.push(Object.assign(this.field.form?.value[<string>this.field.key] ?? {}, this.lookupLabels));
        Object.assign(this.rawData, localArray);
        this._selectedRow = row = 0;
      }
    }

    let parameters = this._serverParameters(this._selectedRow > -1 ? 'update' : 'insert');
    // clear the insert table values now that they are copied to the data
    this.lookupLabels = {};

    this.sendUpdate(action, parameters);
  }

  /**
   * method to send updates to the service engine
   * @param action 
   * @param parameters 
   */
  sendUpdate(action: string, parameters: any) {
    if (!(<any>this.formControl).size) { (<any>this.formControl).size = 0; }
    try {
      // Send data here and add keys for inserts (works for update/insert)
      this.http.post<any[]>(action, parameters).subscribe({
        error: (err) => {
          this.contextService.changeMessage('Error updating values');
          console.error(err);
          return err;
        },
        next: (results: any[] = []) => {
          switch (parameters.data.type) {
            case 'insert':
              // Add a rows and increment the size
              (<any>this.formControl).size++;
              this.setData(results); //results
              break;
            case 'delete':
              // Add a rows and decrement the size
              (<any>this.formControl).size = results.length;
              this.setData(results);
              this._selectedRow = -1; // The row is gone, no need to unset it
              break;
            case 'update':
              this.setData(results); //results
              break;
          }
          // Now we can clear the form and refresh the data
          this._resetForm();
        }
      });
    } catch (err) {
      console.error('Unable to post table change %O\n%O', parameters, err);
    }
  }


  // UI methods 
  get addButton(): any {
    return this.buttons[this._selectedRow == -1 ? 'create' : 'update'];
  }
  get deleteButton(): any {
    return this.buttons.delete;
  }
  get cancelButton(): any {
    return this.buttons.cancel;
  }
  get editButton(): any {
    return this.buttons.edit;
  }


  getOptionLabel(defn: FormlyFieldConfig, value: string): string {
    return defn.props?.sortableOptions.getOption(value)?.label ?? '';
  }
  getOptionValue(defn: FormlyFieldConfig, value: string): string {
    return defn.props?.sortableOptions.getOption(value)?.value ?? '';
  }

  translateWeek(binaryString: string) {
    let binaryDayArray = [];
    let daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    let meetingDaysArray: string[] = [];
    let displayDaysString = '';

    binaryDayArray = binaryString.split('');
    binaryDayArray.forEach(function (day, i) {
      if (parseInt(binaryDayArray[i]) == 1) {
        meetingDaysArray.push(daysOfWeek[i]);
      }
    })
    displayDaysString = meetingDaysArray?.join(', ');
    return displayDaysString;
  }

  showAdditionalData(rowData: any, type: string) {
    let displayColumns = [...this.dataColumns];
    let additionalData = this.dialog.open(AdditionalDataComponent,
      {
        panelClass: 'dialog-form',
        closeOnNavigation: true,
        data: { fields: displayColumns, model: { ...rowData }, label: this.props.label, definition: this, type: type },
        disableClose: false,
        role: 'dialog'
      });

    // Ok, once we get the changed data, merge it with the standard workflow and send the update
    additionalData.afterClosed().subscribe((formEntry: any) => {
      if (formEntry != '' && !!formEntry) {
        // If there is a selected row, this is an update
        if (this._selectedRow > -1) {
          // Set the updated data into the model (this catches the labels)
          this.rawData[this._selectedRow] = (Object.assign(this.rawData[this._selectedRow], formEntry, this.lookupLabels));
        } else { // Otherwise, push the data in the rawData
          // need to get any values in the typeaheads... 
          this._selectedRow = this.rawData.push(Object.assign(formEntry, this.lookupLabels)) - 1;
        }
        // Send the update to the service, the return will be a new set of data to display
        this.sendUpdate('$service/formdata', this._serverParameters(formEntry._action, formEntry));
      } else {
        this._resetForm();
      }
    });
    return false;
  }

  performButtonAction(e: Event, columnDefn: FormlyFieldConfig | null, row: any, index: number) {
    // where do we get the actual function name to call?
    if (!row._index) {
      row._index = index; // best guess
    }
    let buttonActionLabel = columnDefn?.props?.label;
    // prevent bleed through to actions on the row
    e.stopPropagation();
    this._selectRow(row._index, true);
    if (columnDefn?.type == 'file' && columnDefn?.props) {
      columnDefn.props.rowNumber = index;
    }
    // TODO: replace with special component 
    if (buttonActionLabel === 'Select Class') {
      let addToCartDialog = this.dialog.open(DialogComponent,
        {
          disableClose: false,
          autoFocus: true,
          // panelClass: 'custom-dialog',
          width: 'auto',
          height: 'auto',
          hasBackdrop: true,
          data: { textContent: 'Successfully Added to Cart' },
          role: 'dialog'
        });
      this.contextService.updateCartCount();
    } else if (!!columnDefn?.props?.additionalData) {
      this.showAdditionalData(row, 'detail');
    } else if (columnDefn?.props?.self) {
      columnDefn.props.self.onClick(e, row);
    } else {
      console.log("performButtonAction EMPTY ACTION");
    }
  }

  getHrefValue(columnDef: FormlyFieldConfig, row: any): string {
    return (columnDef.props?.hrefPrefix ?? '') + this.getCellDisplay(columnDef, row);
  }
  getCellTitle(columnDef: FormlyFieldConfig, row: any): string {
    return columnDef.props?.label + ': ' + this.getCellDisplay(columnDef, row);
  }
  getCellDisplay(columnDef: FormlyFieldConfig, row: any, rowIndex: number = -1, tzValue: string | null = null): any {
    if (columnDef.hide && !columnDef.props?.includeHidden) { return '' };
    let value = row[<string>columnDef.key];
    switch (columnDef.type) {
      case 'textarea':
        try {
          value = !!value ? JSON.stringify(JSON.parse(value), null, 2) : '';
        } catch (err) { }
        break;
      case 'checkbox':
        value = [true, 'true', 1, '1'].includes(value) ? true : false;
        break;
      case 'phone':
        if (!!value && value != '') {
          value = '(' + value.substring(0, 3) + ') ' + value.substring(3, 6) + '-' + value.substring(6);
        }
        break;
      case 'datepicker':
      case 'date':
        value = (tzValue == 'UTC' ? new Date(value).toISOString() : this.datePipe.transform(value));
        break;
      case 'select':
        if (columnDef.props?.lookupRemote === 'Countries') {
          value = this.getOptionValue(columnDef, value);
        } else {
          value = this.getOptionLabel(columnDef, value);
        }
        break;
      case 'week':
        value = this.translateWeek(value);
        break;
      case 'file':
        value = (this.tableData?.data?.length ?? 0) > 0 ? !!row[<string>columnDef.key] ? 'check' : 'upload' : '';
        break;
      default: // need translation?
        value = row[!!columnDef.props?.saveField ?
          columnDef.props.saveField.split('.', 2).pop() : columnDef.key];
        break;
    }
    return value;
  }

  /** This filters the table of values by the string value, no type matching */
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    if (this.tableData && this.tableData.data?.length > 0) {
      this.tableData.filter = filterValue?.trim().toLowerCase();
    }
  }

  applyDateFilter() {
    if (!!this.props.filterDate) {
      this._startDate = new Date(this.dateModel._searchDate?._startFilter ?? '1900-01-01');
      this._endDate = new Date(this.dateModel._searchDate?._endFilter ?? `${(new Date()).getUTCFullYear() + 10}-12-31`);
    }
    if (this.tableData) {
      this.tableData.filter = this.props.filterDate;
    }
  }

  exportToCSV() {
    // 1. Get table data (use dataSource.data for MatTableDataSource)  
    if (!this.rawData.length) {
      return;
    }

    // 2. Extract headers from the data columns  
    const headers: any[] = this.dataColumns.filter(fcData => {
      return fcData.type != 'button' && !(<string>fcData.key).includes('key.') && (!fcData.hide || !!fcData.props?.exportOnly || !!fcData.props?.includeHidden);
    });

    // 3. Convert data to CSV string  
    let csvContent = headers.map((header: any) => header.props?.exportLabel ?? header.props.label).join(',') + '\n'; // Header row  
    this.rawData.forEach((row, index) => {
      if (this.tableData && this.tableData.filterPredicate(row, this.props.filterDate)) {
        // Map each row to a CSV row (handle nested objects if needed)  
        const values = headers.map(header => {
          let value = this.getCellDisplay(header, row, index, 'UTC') ?? '';
          switch (header.type) {
            case 'checkbox':
              value = !!value ? 'Y' : 'N';
              break;
          }
          // Escape commas and newlines in values  
          return `"${value?.toString().replace(/"/g, '""')}"`;
        });
        csvContent += values.join(',') + '\n';
      }
    });
    // 4. Create a Blob and trigger download  
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `${this.props.label}-${new Date().toISOString().slice(0, 10)}.csv`);
    a.style.visibility = 'hidden';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

/**
 * Additional Data Dialog component. This takes the form definition of the enclosing 
 * group and uses it to display a read-only single row set of values.
 * 
 * This can be used with any FormlyFieldConfig context.
 */
@Component({
  selector: 'additional-data-dialog',
  templateUrl: './additional-data.dialog.html',
  styleUrls: ['./group-list.component.scss'],
  standalone: false
})

export class AdditionalDataComponent implements AfterViewInit {
  parentComponent: GroupListComponent;
  model: any = {};
  form: FormArray = new FormArray<any>([]);
  parentField: FormlyFieldConfig;
  dialogGroup: FormlyFieldConfig;
  fieldsToDisable: any = {};
  dialogFields: FormlyFieldConfig[] = [];
  _selectedRow: number;
  options: FormlyFormOptions;
  dialogType: string;

  constructor(@Inject(MAT_DIALOG_DATA) public data: { fields: FormlyFieldConfig[],  model: any, label: string, definition: GroupListComponent, type: string }, private dialogRef: MatDialogRef<AdditionalDataComponent>) {
    this.parentComponent = data.definition;
    this.options = this.parentComponent.options;
    this.parentField = this.parentComponent.field;
    this._selectedRow = data.model._index ?? -1;
    let keyName = this.parentField.key + '_grp'
    this.model[keyName] = this._convertValues(data.model);

    // Disable any of the uniqueOption values already in the options based on the parent model 
    data.fields.forEach((fldDefn:FormlyFieldConfig) => {
      if ( fldDefn.props?.uniqueOption ) {
         fldDefn.props?.sortableOptions?.disable( );
        const aUsed: string[] = data.definition.tableData.data.map( (dataRow : any, index: number) => this._selectedRow == index ? null:  dataRow[<string>fldDefn.key] ).filter( val => val !== null);
        this.fieldsToDisable[<string>fldDefn.key] = aUsed;
        fldDefn.props?.sortableOptions?.disable( aUsed );
        console.log('Disabling list %O', this.fieldsToDisable);
      }
    });
    this.dialogGroup = {
      key: this.parentField.key + '_grp',
      fieldGroupClassName: 'display-flex',
      fieldGroup: [...data.fields],
    };
    this.dialogFields = [this.dialogGroup];
    this.dialogType = data.type;
  }

  ngAfterViewInit(): void {
    //   this.model[<string>this.dialogGroup.key] = { ... this.data.model };
    this.dialogGroup.form?.updateValueAndValidity();
  }

  _flattenData(model: any): any {
    let newModel: any = {};
    for (const [key, entry] of Object.entries(model)) {
      try {
        let value: any = entry;
        if (value && !(value instanceof Date) && typeof value === 'object') {
          Object.keys(value).forEach(subKey => {
            newModel[key + '.' + subKey] = value[subKey]; // Flatten the object
          });
        } else {
          newModel[key] = value; // Copy the key values
        }
      } catch (err) {
        console.error('Error flattening model key %s: %O', key, err);
      }
    };
    return newModel;
  }
  _convertValues(model: any): any {
    let newModel: any = {};
    if (!!model) {
      for (const key in model) {
        if (key.startsWith('key.')) {
          newModel[key] = model[key]; // Copy the key values
          continue; // Skip the key values
        }
        let propStack = key.split('.'), lastProp = propStack.pop(), currentProp, currentModel = newModel;
        while ((currentProp = propStack.shift())) {
          if (!currentModel[currentProp]) {
            currentModel = currentModel[currentProp] = {};
          } else {
            currentModel = currentModel[currentProp];
          }
        }
        if (lastProp) { currentModel[lastProp] = model[key]; }
      }
    }
    return newModel;
  }

  resetForm() {
    this.model[<string>this.dialogGroup.key] = this._convertValues(this.data.model);
    let fg = (<FormGroup>this.parentField.formControl);
    fg.reset({});
  }

  updateData(action: string) {
    this.dialogRef.close(Object.assign(this._flattenData(this.model[<string>this.dialogGroup.key]), { _action: action }));
  }
  isButton(field: FormlyFieldConfig) {
    return (field.type == 'button');
  }

  get isDetail() {
    return (this.dialogType == 'detail');
  }
}